import i18n from '@/languages'

export interface ApiErrorResponse {
  code?: number | string
  msg?: string
  message?: string
  errorKey?: string
  args?: unknown[]
  alreadyNotified?: boolean
  raw?: unknown
}

export class ApiResponseError extends Error {
  code?: number | string
  errorKey?: string
  args?: unknown[]
  alreadyNotified: boolean
  raw?: unknown

  constructor(error: ApiErrorResponse, alreadyNotified = false) {
    super(getApiErrorMessage(error))
    this.name = 'ApiResponseError'
    this.code = error.code
    this.errorKey = error.errorKey
    this.args = error.args
    this.alreadyNotified = alreadyNotified
    this.raw = error.raw ?? error
  }
}

const normalizeArgs = (args?: unknown[]) => {
  if (!args || !Array.isArray(args) || args.length === 0) {
    return []
  }
  return args.map((item) => (item == null ? '' : item))
}

const hasMessageKey = (key: string) => {
  const globalI18n = i18n.global as any
  return typeof globalI18n.te === 'function' ? globalI18n.te(key) : false
}

export const getApiErrorMessage = (error: unknown, fallback?: string): string => {
  const data = (error instanceof ApiResponseError ? error.raw : error) as ApiErrorResponse | undefined
  const code = data?.code ?? (error as ApiErrorResponse | undefined)?.code
  const args = normalizeArgs(data?.args ?? (error as ApiErrorResponse | undefined)?.args)
  // Some legacy backend paths reuse global code 400 with a specific message
  // instead of a dedicated ErrorCode. Keep those readable instead of forcing
  // the generic "Request parameter is invalid" translation.
  if (String(code) === '400' && args.length === 0 && data?.msg) {
    return data.msg
  }
  const explicitKey = data?.errorKey ?? (error as ApiErrorResponse | undefined)?.errorKey
  const candidateKeys = [
    explicitKey,
    code !== undefined && code !== null ? `apiErrors.${code}` : undefined
  ].filter(Boolean) as string[]

  for (const key of candidateKeys) {
    if (hasMessageKey(key)) {
      return i18n.global.t(key, args as any)
    }
  }

  return (
    data?.message ||
    data?.msg ||
    (error instanceof Error ? error.message : '') ||
    fallback ||
    i18n.global.t('apiErrors.default')
  )
}

export const createApiResponseError = (
  error: ApiErrorResponse,
  alreadyNotified = false
): ApiResponseError => {
  return new ApiResponseError(error, alreadyNotified)
}

export const isApiErrorAlreadyNotified = (error: unknown): boolean => {
  return error instanceof ApiResponseError && error.alreadyNotified
}

export const showApiErrorMessage = (
  notify: (message: string) => void,
  error: unknown,
  fallback?: string
) => {
  if (!isApiErrorAlreadyNotified(error)) {
    notify(getApiErrorMessage(error, fallback))
  }
}
